<template>
    <Loading :overlay="true" overlayText="Criando conta..." :show="loading" />
    <div class="min-h-screen grid lg:grid-cols-2">
        <!-- Left: form -->
        <div class="bg-white flex flex-col min-h-screen px-6 sm:px-10 lg:pl-[130px] lg:pr-10 pt-6">
            <div class="flex-1 flex flex-col justify-center w-full max-w-[510px] mx-auto lg:mx-0 py-6">
                <img src="../assets/logo.png" class="w-40 sm:w-44 mb-5" alt="SIRIMMATRIX" />

                <template v-if="submitted">
                    <div class="w-12 h-12 sm:w-14 sm:h-14 mb-5 rounded-full bg-emerald-600/10 flex items-center justify-center text-emerald-600">
                        <svg class="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                        </svg>
                    </div>
                    <h1 class="text-[22px] sm:text-[24px] font-medium leading-[1.2] text-[#101414] mb-3">Confirme seu email</h1>
                    <p class="text-[15px] sm:text-[16px] text-[#333A44] leading-[1.7] mb-6 max-w-[500px]">
                        Enviamos um link de confirmação para <strong>{{ submittedEmail }}</strong>. Clique no link para ativar sua conta e começar a usar a SIRIMMATRIX.
                    </p>
                    <button
                        type="button"
                        @click="resend"
                        :disabled="resending"
                        class="text-[15px] font-medium text-cyan-600 hover:underline disabled:opacity-60 text-left"
                    >
                        {{ resending ? 'Reenviando...' : 'Não recebeu? Reenviar email' }}
                    </button>
                    <p v-if="resendMessage" class="text-emerald-600 text-sm mt-3">{{ resendMessage }}</p>

                    <router-link :to="{ name: 'login' }" class="block mt-7 text-[15px] font-medium text-cyan-600 hover:underline">Voltar ao login</router-link>
                </template>

                <template v-else>
                <h1 class="text-[22px] sm:text-[24px] font-medium leading-[1.2] text-[#101414] mb-3">Crie sua conta</h1>
                <p class="text-[15px] sm:text-[16px] text-[#333A44] leading-[1.6] mb-4 max-w-[500px]">
                    Comece agora a avaliar riscos de lesão com a SIRIMMATRIX.
                </p>
                <div class="mb-6">
                    <span class="inline-flex items-center gap-1.5 text-[13px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                        7 dias grátis para testar
                    </span>
                </div>

                <form @submit.prevent="submit">
                    <!-- Name -->
                    <div class="mb-6">
                        <label class="block text-[16px] font-medium text-[#171717] mb-2.5">Nome</label>
                        <div class="relative">
                            <span class="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-600">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                                </svg>
                            </span>
                            <input
                                type="text"
                                v-model="user.name"
                                maxlength="100"
                                placeholder="Seu nome"
                                @blur="v$.name.$touch"
                                class="w-full h-[58px] pl-12 pr-4 text-base bg-white border border-[#D9D9D9] rounded-xl text-[#171717] placeholder:text-gray-400 focus:outline-none focus:border-cyan-600 focus:ring-2 focus:ring-cyan-600/15 transition-colors"
                            />
                        </div>
                        <div v-for="error in v$.$errors" :key="error.$uid">
                            <BaseErrorMessage v-if="error.$property === 'name'" :message="error.$message" />
                        </div>
                    </div>

                    <!-- Email -->
                    <div class="mb-6">
                        <label class="block text-[16px] font-medium text-[#171717] mb-2.5">E-mail</label>
                        <div class="relative">
                            <span class="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-600">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                                </svg>
                            </span>
                            <input
                                type="email"
                                inputmode="email"
                                v-model="user.email"
                                maxlength="60"
                                placeholder="seuemail@email.com"
                                @blur="v$.email.$touch"
                                class="w-full h-[58px] pl-12 pr-4 text-base bg-white border border-[#D9D9D9] rounded-xl text-[#171717] placeholder:text-gray-400 focus:outline-none focus:border-cyan-600 focus:ring-2 focus:ring-cyan-600/15 transition-colors"
                            />
                        </div>
                        <div v-for="error in v$.$errors" :key="error.$uid">
                            <BaseErrorMessage v-if="error.$property === 'email'" :message="error.$message" />
                        </div>
                    </div>

                    <!-- Password -->
                    <div class="mb-6">
                        <label class="block text-[16px] font-medium text-[#171717] mb-2.5">Senha</label>
                        <div class="relative">
                            <span class="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-600">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 0h10.5a2.25 2.25 0 012.25 2.25v6.75a2.25 2.25 0 01-2.25 2.25H6.75a2.25 2.25 0 01-2.25-2.25v-6.75a2.25 2.25 0 012.25-2.25z" />
                                </svg>
                            </span>
                            <input
                                :type="showPassword ? 'text' : 'password'"
                                v-model="user.password"
                                maxlength="80"
                                placeholder="Crie sua senha"
                                class="w-full h-[58px] pl-12 pr-12 text-base bg-white border border-[#D9D9D9] rounded-xl text-[#171717] placeholder:text-gray-400 focus:outline-none focus:border-cyan-600 focus:ring-2 focus:ring-cyan-600/15 transition-colors"
                            />
                            <button
                                type="button"
                                @click="showPassword = !showPassword"
                                class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-600 transition-colors"
                                tabindex="-1"
                            >
                                <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.93 3.93m0 0a3 3 0 104.243 4.243m-4.243-4.243L17.577 17.577m-4.243-4.243l4.243 4.243" />
                                </svg>
                                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </button>
                        </div>

                        <!-- Strength meter -->
                        <div v-if="user.password" class="mt-3 rounded-xl border border-gray-100 bg-slate-50 p-3">
                            <div class="flex items-center justify-between gap-2 mb-2">
                                <span class="text-xs font-semibold uppercase tracking-wide text-gray-400">Força da senha</span>
                                <span class="text-xs font-semibold" :class="passwordStrength.valid ? 'text-emerald-600' : 'text-gray-500'">
                                    {{ passwordStrength.label }}
                                </span>
                            </div>
                            <div class="h-2 rounded-full bg-white overflow-hidden border border-gray-100">
                                <div class="h-full rounded-full transition-all duration-300" :class="passwordStrength.tone" :style="{ width: passwordStrength.width + '%' }"></div>
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                                <div
                                    v-for="item in passwordStrength.checks"
                                    :key="item.key"
                                    class="text-xs rounded-lg px-2 py-1.5 border"
                                    :class="item.met ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-500'"
                                >
                                    {{ item.met ? 'OK' : 'Falta' }}: {{ item.label }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Confirm password -->
                    <div class="mb-2">
                        <label class="block text-[16px] font-medium text-[#171717] mb-2.5">Confirmar senha</label>
                        <div class="relative">
                            <span class="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-600">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 0h10.5a2.25 2.25 0 012.25 2.25v6.75a2.25 2.25 0 01-2.25 2.25H6.75a2.25 2.25 0 01-2.25-2.25v-6.75a2.25 2.25 0 012.25-2.25z" />
                                </svg>
                            </span>
                            <input
                                :type="showConfirmPassword ? 'text' : 'password'"
                                v-model="confirmPassword"
                                maxlength="80"
                                placeholder="Repita sua senha"
                                @keyup.enter="submit"
                                class="w-full h-[58px] pl-12 pr-12 text-base bg-white border border-[#D9D9D9] rounded-xl text-[#171717] placeholder:text-gray-400 focus:outline-none focus:border-cyan-600 focus:ring-2 focus:ring-cyan-600/15 transition-colors"
                            />
                            <button
                                type="button"
                                @click="showConfirmPassword = !showConfirmPassword"
                                class="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-600 transition-colors"
                                tabindex="-1"
                            >
                                <svg v-if="showConfirmPassword" class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.93 3.93m0 0a3 3 0 104.243 4.243m-4.243-4.243L17.577 17.577m-4.243-4.243l4.243 4.243" />
                                </svg>
                                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </button>
                        </div>
                        <p v-if="confirmPassword && user.password !== confirmPassword" class="text-red-500 text-xs mt-2">As senhas não coincidem</p>
                    </div>

                    <!-- Primary button -->
                    <button
                        type="submit"
                        :disabled="loading"
                        class="w-full h-[58px] mt-5 rounded-xl bg-cyan-600 text-white font-medium text-base hover:bg-cyan-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
                    >
                        <svg v-if="loading" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ loading ? 'Criando...' : 'Criar conta' }}
                    </button>

                    <!-- OR separator -->
                    <div class="relative flex items-center my-7">
                        <div class="flex-grow border-t border-[#E0E0E0]"></div>
                        <span class="mx-4 text-[13px] text-[#333A44] font-medium">ou</span>
                        <div class="flex-grow border-t border-[#E0E0E0]"></div>
                    </div>

                    <!-- Google -->
                    <button
                        type="button"
                        @click="signupWithGoogle"
                        :disabled="loadingGoogle"
                        class="w-full h-[58px] rounded-xl bg-white border border-[#DCDCDC] text-[#171717] font-medium text-base hover:bg-gray-50 disabled:opacity-60 transition-colors flex items-center justify-center gap-3.5"
                    >
                        <svg v-if="loadingGoogle" class="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <svg v-else viewBox="0 0 24 24" class="h-5 w-5" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        {{ loadingGoogle ? 'Aguarde...' : 'Cadastre-se com Google' }}
                    </button>

                    <div class="flex justify-center mt-2">
                        <BaseErrorMessage :message="formError" />
                    </div>

                    <p class="text-center text-[15px] text-[#333A44] mt-7">
                        Já tem uma conta?
                        <router-link :to="{ name: 'login' }" class="font-medium text-cyan-600 hover:underline ml-1">Entrar</router-link>
                    </p>
                </form>
                </template>
            </div>
        </div>

        <!-- Right: branding -->
        <AuthBrandingPanel />
    </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import router from "@/router";
import { useVuelidate } from '@vuelidate/core';
import AuthService from '../services/AuthService';
import GoogleAuthService from '../services/GoogleAuthService';
import AuthBrandingPanel from '../components/AuthBrandingPanel.vue';
import Swal from 'sweetalert';
import { requiredMessage, emailMessage } from '../services/VuelidateMessageService';
import { getPasswordStrengthMeta } from '@/utils/password';

export default {
    name: "signup",
    components: {
        AuthBrandingPanel
    },
    setup() {
        let loading = ref(false);
        let loadingGoogle = ref(false);
        const user = ref({});
        const confirmPassword = ref('');
        const showPassword = ref(false);
        const showConfirmPassword = ref(false);
        const formError = ref('');
        const submitted = ref(false);
        const submittedEmail = ref('');
        const resending = ref(false);
        const resendMessage = ref('');
        const googleClientId = process.env.VUE_APP_GOOGLE_CLIENT_ID || null;

        const userRules = computed(() => ({
            name: { required: requiredMessage },
            email: { required: requiredMessage, email: emailMessage },
        }));

        const passwordStrength = computed(() => getPasswordStrengthMeta(user.value.password));

        onMounted(async () => {
            clearlocalStorage();
        });

        function clearlocalStorage() {
            localStorage.clear();
        }

        async function submit() {
            formError.value = '';
            const isValid = await v$.value.$validate();

            if (!isValid) return;

            if (!passwordStrength.value.valid) {
                formError.value = 'A senha ainda não atende todos os requisitos de segurança.';
                return;
            }
            if (user.value.password !== confirmPassword.value) {
                formError.value = 'As senhas não coincidem.';
                return;
            }

            await save(user.value);
        }

        async function save(user) {
            try {
                loading.value = true;
                const { data: { data } } = await AuthService.createAccount(user);

                submittedEmail.value = data.email;
                submitted.value = true;
                loading.value = false;
            } catch(err) {
                loading.value = false;

                if (err.response?.data?.error === 'already_exist') {
                    Swal({
                        title: 'E-mail já cadastrado',
                        text: 'Já existe uma conta com esse e-mail. Faça Login',
                        icon: 'warning',
                    });
                } else if (err.response?.data?.error === 'weak_password') {
                    formError.value = err.response.data.text || 'Senha fora do padrão de segurança.';
                } else {
                    Swal({
                        title: 'Erro ao criar a conta',
                        text: err.response?.data?.text || 'Tente novamente',
                        icon: 'error',
                    });
                }
            }
        }

        async function resend() {
            resending.value = true;
            resendMessage.value = '';
            try {
                await AuthService.resendConfirmation(submittedEmail.value);
                resendMessage.value = 'Email reenviado. Confira sua caixa de entrada.';
            } catch {
                resendMessage.value = 'Erro ao reenviar. Tente novamente em instantes.';
            } finally {
                resending.value = false;
            }
        }

        async function signupWithGoogle() {
            if (!googleClientId) {
                formError.value = 'Cadastro com Google não configurado.';
                return;
            }
            loadingGoogle.value = true;
            formError.value = '';
            try {
                const idToken = await GoogleAuthService.getGoogleIdToken(googleClientId);
                if (!idToken) {
                    formError.value = 'Cadastro com Google cancelado.';
                    return;
                }
                const { data: { data } } = await AuthService.loginWithGoogle(idToken);
                AuthService.storeLocalData({ ...data });
                router.push({ name: 'home' });
            } catch (err) {
                const errCode = err?.response?.data?.error;
                if (errCode === 'email_already_exists') {
                    formError.value = 'Já existe uma conta com esse e-mail.';
                } else {
                    formError.value = 'Erro ao cadastrar com Google. Tente novamente.';
                }
            } finally {
                loadingGoogle.value = false;
            }
        }

        const v$ = useVuelidate(userRules, user);

        return {
            loading,
            loadingGoogle,
            submit,
            signupWithGoogle,
            user,
            confirmPassword,
            showPassword,
            showConfirmPassword,
            formError,
            passwordStrength,
            googleClientId,
            submitted,
            submittedEmail,
            resend,
            resending,
            resendMessage,
            v$
        };
    }
};
</script>
